import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Tooltip } from './common/tooltip.component';
import CurrencyInfoModal from './currency-info-modal.component';
import VatInfoModal from './vat-info-modal.component';
import RutRotInfoModal from './rut-rot-info-modal.component';
import { useAppDispatch } from '../hooks/useRedux';
import { validateCustomerSsn, setSsnValidationFailed } from '../features/customer/customer.slice';
import notify from '../helper/notify';
import './invoice-preferences.scss';
import './rut-rot-info-modal.scss';

interface InvoicePreferencesProps {
  preferences: {
    currency: string;
    vat?: number;  // NEW - VAT percentage
    deductionType?: string;
    showDates?: string;  // Show dates in invoice
    accommodationType?: string;
    brfOrgNumber?: string;
    apartmentHouseNo?: string;
    property?: string;  // NEW - for house type
    weekOffDays?: string;  // NEW - comma-separated week-off days
    dueDate?: string;  // NEW - Due date field
    invoiceDate?: string;  // NEW - Invoice date (printed on invoice)
  };
  onPreferenceChange?: (field: string, value: string) => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
  hasHourlyArticle?: boolean;  // NEW - True when the invoice has >=1 "Payment per Hour" (article 92); RUT/ROT requires it
  customerInfo?: {  // NEW - Customer information for RUT/ROT validation
    accountType?: number;
    country?: string;
    customerId?: number;
  };
  stepLabel?: string;    // NEW - e.g. "Step 3 of 6"
  stepTitle?: string;    // NEW - e.g. "Preferences"
  stepSubtitle?: string; // NEW - e.g. "Currency, VAT & dates"
}

interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  placeholder: string;
  onSelect: (value: string) => void;
  label: string;
  disabled?: boolean;
  tooltipContent?: string;
  loading?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  placeholder,
  onSelect,
  label,
  disabled = false,
  tooltipContent,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    onSelect(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <>
      {label ? (
        <Form.Label className="preference-label">
          {label}
          {tooltipContent && (
            <Tooltip content={tooltipContent} position="top">
              <span className="info-icon">ⓘ</span>
            </Tooltip>
          )}
        </Form.Label>
      ) : null}
      <div className="custom-dropdown" ref={dropdownRef}>
        <div 
          className={`dropdown-trigger ${isOpen ? 'open' : ''} ${disabled || loading ? 'disabled' : ''}`}
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        >
          <span className="dropdown-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {loading ? (
            <svg
              className="dropdown-spinner"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25" />
              <path d="M16 9A7 7 0 0 1 9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg 
              className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
              width="20" 
              height="20" 
              viewBox="0 0 20 20" 
              fill="none"
            >
              <path 
                d="M5 7.5L10 12.5L15 7.5" 
                stroke="currentColor" 
                strokeWidth="1.67" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        
        {isOpen && (
          <div className="dropdown-list">
            <div className="dropdown-content">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`list-item ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <div className="text-input">
                    <div className="paragraph-container">
                      <p className="option-text">{option.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const InvoicePreferences: React.FC<InvoicePreferencesProps> = ({
  preferences,
  onPreferenceChange,
  copiedInvoiceData,
  isEditModeInvoice,
  hasHourlyArticle = false,
  customerInfo,
  stepLabel,
  stepTitle,
  stepSubtitle
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showVatModal, setShowVatModal] = useState(false);
  const [selectedVatForModal, setSelectedVatForModal] = useState<string>('25');
  const [showRutRotInfoModal, setShowRutRotInfoModal] = useState(false);
  const [isValidatingSsn, setIsValidatingSsn] = useState(false);
  
  // Debug: Log when preferences.vat changes
  useEffect(() => {
    console.log('🔍 VAT Debug - preferences.vat changed to:', preferences.vat);
  }, [preferences.vat]);
 
  // Update VAT when customer country changes (only if no copied data and VAT not already set)
  useEffect(() => {
    // Don't auto-set VAT if data has been populated from copiedInvoiceData
    if (customerInfo?.country && !copiedInvoiceData && !isDataPopulatedRef.current && (preferences.vat === undefined || preferences.vat === null)) {
      console.log('🔍 VAT Debug - Auto-setting VAT based on country');
      const newVAT = customerInfo.country === 'Sweden' ? '25' : '0';
      onPreferenceChange?.('vat', newVAT);
    }
  }, [customerInfo?.country, copiedInvoiceData, preferences.vat]);
  
  // Helper function to format ISO date to YYYY-MM-DD format
  const formatDateForInput = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    try {
      // Handle ISO format with time (2026-01-15T00:00:00)
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateStr);
      return '';
    }
  };
  
  // Track if data has been populated to prevent re-render
  const isDataPopulatedRef = React.useRef(false);
  
  // Initialize preferences from copiedInvoiceData when it changes
  useEffect(() => {
    console.log('🔍 VAT Debug - copiedInvoiceData effect running, isDataPopulatedRef:', isDataPopulatedRef.current);
    // Only populate if we have copiedInvoiceData and haven't already populated it
    if (copiedInvoiceData && !isDataPopulatedRef.current) {
      console.log('🔍 VAT Debug - Populating data from copiedInvoiceData');
      // Set currency if available
      if (copiedInvoiceData.currency) {
        onPreferenceChange?.('currency', copiedInvoiceData.currency);
      }
      
      // Set VAT if available
      if (copiedInvoiceData.vat !== undefined || copiedInvoiceData.vatPercent !== undefined) {
        const vatValue = copiedInvoiceData.vat ?? copiedInvoiceData.vatPercent;
        console.log('🔍 VAT Debug - copiedInvoiceData.vat:', copiedInvoiceData.vat);
        console.log('🔍 VAT Debug - copiedInvoiceData.vatPercent:', copiedInvoiceData.vatPercent);
        console.log('🔍 VAT Debug - vatValue being set:', vatValue);
        console.log('🔍 VAT Debug - Setting isDataPopulatedRef to true');
        onPreferenceChange?.('vat', vatValue.toString());
      }
      
      // Set due date if available
      if (copiedInvoiceData.dueDate) {
        const formattedDueDate = formatDateForInput(copiedInvoiceData.dueDate);
        onPreferenceChange?.('dueDate', formattedDueDate);
      }

      // Set show dates from API value (true/false) instead of always defaulting to 'yes'
      if (copiedInvoiceData.showDates !== undefined && copiedInvoiceData.showDates !== null) {
        const showDatesValue = copiedInvoiceData.showDates === true || copiedInvoiceData.showDates === 'true' ? 'yes' : 'no';
        onPreferenceChange?.('showDates', showDatesValue);
      }
      
      // Set deduction type based on ROT/RUT
      if (copiedInvoiceData.rotDeduction || copiedInvoiceData.rootDeduction) {
        onPreferenceChange?.('deductionType', 'rot');
      } else if (copiedInvoiceData.rutDeduction) {
        onPreferenceChange?.('deductionType', 'rut');
      }
      
      // Set accommodation type if ROT is selected
      if (copiedInvoiceData.rotDeduction || copiedInvoiceData.rootDeduction) {
        if (copiedInvoiceData.house) {
          onPreferenceChange?.('accommodationType', 'house');
          const propertyValue = copiedInvoiceData.rotApartmentNo || copiedInvoiceData.property || '';
          if (propertyValue) {
            onPreferenceChange?.('property', propertyValue);
          }
        } else if (copiedInvoiceData.apartment) {
          onPreferenceChange?.('accommodationType', 'apartment');
          if (copiedInvoiceData.brfNo) {
            onPreferenceChange?.('brfOrgNumber', copiedInvoiceData.brfNo);
          }
          if (copiedInvoiceData.rotApartmentNo) {
            onPreferenceChange?.('apartmentHouseNo', copiedInvoiceData.rotApartmentNo);
          }
        }
      }
      
      // Mark as populated to prevent re-running
      isDataPopulatedRef.current = true;
     }
  }, [copiedInvoiceData]);
  
  // Reset the ref when copiedInvoiceData changes to a new object
  useEffect(() => {
    if (copiedInvoiceData) {
      isDataPopulatedRef.current = false;
    }
  }, [copiedInvoiceData?.invoiceId, copiedInvoiceData?.id]);
  
  // Currency options - matching InvoiceTool.js (comes from globalData.currencies)
  // All supported currencies in the system as per translation files and InvoiceTool.js
  const currencyOptions = [
    { value: 'SEK', label: 'SEK' },
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CHF', label: 'CHF' },
    { value: 'CAD', label: 'CAD' },
    { value: 'AUD', label: 'AUD' },
    { value: 'NOK', label: 'NOK' },
    { value: 'DKK', label: 'DKK' },
    { value: 'JPY', label: 'JPY' },
    { value: 'CNY', label: 'CNY' },
  ];

  // VAT options - standard VAT rates
  const vatOptions = [
    { value: '0', label: '0%' },
    { value: '6', label: '6%' },
    { value: '12', label: '12%' },
    { value: '25', label: '25%' },
  ];

  // Show Dates options
  const showDatesOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') },
  ];

  // RUT/ROT Deduction type options - matching item-details.component.tsx
  const deductionTypeOptions = [
    { value: 'rot', label: t('ROT_DEDUCTION') },
    { value: 'rut', label: t('RUT_DEDUCTION') },
    { value: 'none', label: t('NONE_REMOVE_RUT_ROT') }
  ];

  // Accommodation type options - removed 'other' option
  const accommodationTypeOptions = [
    { value: 'apartment', label: t('APARTMENT') },
    { value: 'house', label: t('HOUSE') },
  ];

  const handleOptionSelect = (field: string, value: string) => {
    // If deduction type is changing to 'rot', set default accommodation type to 'apartment'
    if (field === 'deductionType' && value === 'rot') {
      // Set apartment as default accommodation type if not already set
      if (!preferences.accommodationType) {
        onPreferenceChange?.('accommodationType', 'apartment');
      }
    }
    
    // If deduction type is changing away from 'rot', reset accommodation fields
    if (field === 'deductionType' && value !== 'rot') {
      onPreferenceChange?.('accommodationType', '');
      onPreferenceChange?.('brfOrgNumber', '');
      onPreferenceChange?.('apartmentHouseNo', '');
      onPreferenceChange?.('property', '');
    }
    
    // If accommodation type is changing, reset specific fields
    if (field === 'accommodationType') {
      if (value === 'apartment') {
        // Reset house-specific field when switching to apartment
        onPreferenceChange?.('property', '');
      } else if (value === 'house') {
        // Reset apartment-specific fields when switching to house
        onPreferenceChange?.('brfOrgNumber', '');
        onPreferenceChange?.('apartmentHouseNo', '');
      }
    }
    // Show currency info modal when currency is changed and it's not SEK
    if (field === 'currency' && value !== 'SEK') {
      setShowCurrencyModal(true);
    }
    // Show VAT info modal when VAT is changed
    if (field === 'vat') {
      setSelectedVatForModal(value);
      setShowVatModal(true);
    }
    
    
    onPreferenceChange?.(field, value);
  };

  // Check if RUT/ROT deduction should be enabled
  // Enabled only when customer is Private (accountType === 1) and country is Sweden
  const isRutRotEnabled = 
    customerInfo?.accountType === 1 && 
    customerInfo?.country === 'Sweden';

  // Handle deductionType selection with SSN validation
  const handleDeductionTypeSelect = async (value: string) => {
    if ((value === 'rot' || value === 'rut') && customerInfo?.customerId) {
      // Convert current i18n language to en-us / sv-se format
      const rawLang = i18n.language || 'en';
      const lang = (rawLang === 'sv' || rawLang === 'sv-SE' || rawLang === 'sv-se') ? 'sv-se' : 'en-us';
      setIsValidatingSsn(true);
      try {
        await dispatch(validateCustomerSsn({ customerId: customerInfo.customerId, lang })).unwrap();
        // Validation passed — proceed with normal selection and show info modal
        handleOptionSelect('deductionType', value);
        setShowRutRotInfoModal(true);
      } catch (err: any) {
        const errorMsg = typeof err === 'string' ? err : (err?.message || (t('INVALID_SOCIAL_SECURITY_NUMBER') || 'Invalid social security number'));
        toast.error(errorMsg as string);
        // Signal customer-details to highlight Edit Customer button
        dispatch(setSsnValidationFailed(true));
        // Revert to none
        onPreferenceChange?.('deductionType', 'none');
      } finally {
        setIsValidatingSsn(false);
      }
    } else {
      handleOptionSelect('deductionType', value);
    }
  };

  // DEBUG: Log RUT/ROT enablement status
  // Automatically reset deductionType to 'none' if conditions are not met
  // BUT skip reset when loading copied invoice data (edit mode)
  React.useEffect(() => {
    if (!copiedInvoiceData && !isRutRotEnabled && preferences.deductionType && preferences.deductionType !== 'none') {
      onPreferenceChange?.('deductionType', 'none');
    }
  }, [isRutRotEnabled, preferences.deductionType, onPreferenceChange, copiedInvoiceData]);

  // When the LAST "Payment per Hour" article is removed while a RUT/ROT deduction is selected,
  // warn the user (explaining why) and then clear the deduction. RUT/ROT requires an hourly article.
  const prevHasHourlyArticleRef = useRef<boolean>(hasHourlyArticle);
  React.useEffect(() => {
    const hadHourlyArticle = prevHasHourlyArticleRef.current;
    prevHasHourlyArticleRef.current = hasHourlyArticle;

    const rutRotSelected = preferences.deductionType === 'rot' || preferences.deductionType === 'rut';

    // Only on the transition from having an hourly article to not having one (user removed it)
    if (hadHourlyArticle && !hasHourlyArticle && rutRotSelected && !isEditModeInvoice) {
      notify.warning(
        (t('ROT_RUT_REMOVED_TITLE') || 'ROT/RUT deduction removed') as string,
        (t('ROT_RUT_REMOVED_MESSAGE') ||
          'You have removed the Payment per Hour article. The ROT/RUT deduction will also be removed, because at least one Payment per Hour article is required to apply ROT/RUT.') as string,
        {
          okLabel: (t('OK') || 'OK') as string,
          onOk: () => handleOptionSelect('deductionType', 'none'),
        }
      );
    }
  }, [hasHourlyArticle, preferences.deductionType, isEditModeInvoice]);

  // Show accommodation fields only when ROT deduction is selected
  const isROTSelected = preferences.deductionType === 'rot';
  
  // Check accommodation type - default to 'apartment' if not set
  const currentAccommodationType = preferences.accommodationType || 'apartment';
  const isApartmentSelected = currentAccommodationType === 'apartment';
  const isHouseSelected = currentAccommodationType === 'house';

  // Get default VAT based on customer country
  const getDefaultVAT = (): string => {
    console.log('🔍 VAT Debug - preferences.vat:', preferences.vat);
    console.log('🔍 VAT Debug - customerInfo?.country:', customerInfo?.country);
    const vatValue = preferences.vat !== undefined && preferences.vat !== null 
      ? preferences.vat.toString() 
      : (customerInfo?.country === 'Sweden' ? '25' : '0');
    console.log('🔍 VAT Debug - getDefaultVAT returning:', vatValue);
    return vatValue;
  };

  return (
    <>
      <VatInfoModal 
        show={showVatModal} 
        onHide={() => setShowVatModal(false)}
        selectedVat={selectedVatForModal}
      />
      <CurrencyInfoModal 
        show={showCurrencyModal} 
        onHide={() => setShowCurrencyModal(false)} 
      />
      <RutRotInfoModal
        show={showRutRotInfoModal}
        onHide={() => setShowRutRotInfoModal(false)}
      />
      <Card className="invoice-preferences-card invoice-preferences pref-outer">
        <Card.Body className="preferences-body pref-outer-body">
        {/* Step header */}
        {(stepTitle || stepLabel) && (
          <div className="cust-step-header">
            <div className="cust-step-heading iw-head-with-icon">
              <span className="iw-head-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>
              </span>
              <div className="iw-head-text">
                {stepLabel && (
                  <span className="cust-step-pill"><span className="cust-step-dot" />{stepLabel}</span>
                )}
                {stepTitle && <h2 className="cust-step-title">{stepTitle}</h2>}
                {stepSubtitle && <p className="cust-step-sub">{stepSubtitle}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="pref-grid">
          {/* Currency */}
          <div className="pref-card" data-tour="pref-currency">
            <div className="pref-card-head">
              <span className="pref-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M14.5 9.2c-.5-.8-1.5-1.2-2.5-1.2-1.4 0-2.5.8-2.5 2s1.1 1.7 2.5 2 2.5.8 2.5 2-1.1 2-2.5 2c-1 0-2-.4-2.5-1.2M12 6.5v11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              </span>
              <div className="pref-card-titles">
                <span className="pref-card-label">{t('CURRENCY')}
                  <Tooltip content={t('CURRENCY_TOOLTIP')} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                </span>
                <span className="pref-card-helper">{t('PREF_CURRENCY_HELPER')}</span>
              </div>
            </div>
            <div className="pref-card-control">
              <CustomDropdown
                options={currencyOptions}
                value={preferences.currency}
                placeholder="SEK"
                onSelect={(value) => handleOptionSelect('currency', value)}
                label=""
              />
            </div>
          </div>

          {/* VAT */}
          <div className="pref-card" data-tour="pref-vat">
            <div className="pref-card-head">
              <span className="pref-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M19 5L5 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><circle cx="7.5" cy="7.5" r="2.5" stroke="currentColor" strokeWidth="1.8"/><circle cx="16.5" cy="16.5" r="2.5" stroke="currentColor" strokeWidth="1.8"/></svg>
              </span>
              <div className="pref-card-titles">
                <span className="pref-card-label">{t('VAT')}
                  <Tooltip content={t('VAT_TOOLTIP')} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                </span>
                <span className="pref-card-helper">{t('PREF_VAT_HELPER')}</span>
              </div>
            </div>
            <div className="pref-card-control">
              <CustomDropdown
                options={vatOptions}
                value={getDefaultVAT()}
                placeholder="25%"
                onSelect={(value) => handleOptionSelect('vat', value)}
                label=""
              />
            </div>
          </div>

          {/* Due date — only in edit mode (unchanged original condition) */}
          {isEditModeInvoice && (
          <div className="pref-card">
            <div className="pref-card-head">
              <span className="pref-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </span>
              <div className="pref-card-titles">
                <span className="pref-card-label">{t('DUE_DATE')}</span>
                <span className="pref-card-helper">{t('PREF_DUE_DATE_HELPER')}</span>
              </div>
            </div>
            <div className="pref-card-control">
              <Form.Control
                type="date"
                value={formatDateForInput(preferences.dueDate)}
                onChange={(e) => handleOptionSelect('dueDate', e.target.value)}
                className="input-value"
              />
            </div>
          </div>
          )}

          {/* RUT / ROT deduction */}
          <div className="pref-card" data-tour="pref-rutrot">
            <div className="pref-card-head">
              <span className="pref-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L4 17l3 3 5.3-5.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.1-.6-.6-2.1 2.5-2.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
              </span>
              <div className="pref-card-titles">
                <span className="pref-card-label">{t('RUT_ROT_DEDUCTION_TYPE')}
                  <Tooltip content={`${t('RUT_ROT_DEDUCTION_TYPE_TOOLTIP')} ${t('ROT_RUT_HOURLY_REQUIREMENT_NOTE') || 'Note: You can only select ROT/RUT when the invoice has at least one Payment per Hour article.'}`} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                </span>
                <span className="pref-card-helper">{t('PREF_RUTROT_HELPER')}</span>
              </div>
            </div>
            <div className="pref-card-control">
              <CustomDropdown
                options={deductionTypeOptions}
                value={preferences.deductionType || 'none'}
                placeholder={t('NONE_REMOVE_RUT_ROT')}
                onSelect={handleDeductionTypeSelect}
                label=""
                disabled={isEditModeInvoice || !isRutRotEnabled || !hasHourlyArticle}
                loading={isValidatingSsn}
              />
            </div>
          </div>

          {/* Show dates */}
          <div className="pref-card" data-tour="pref-showdates">
            <div className="pref-card-head">
              <span className="pref-card-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
              </span>
              <div className="pref-card-titles">
                <span className="pref-card-label">{t('SHOW_DATES')}</span>
                <span className="pref-card-helper">{t('PREF_SHOW_DATES_HELPER')}</span>
              </div>
            </div>
            <div className="pref-card-control">
              <CustomDropdown
                options={showDatesOptions}
                value={preferences.showDates || 'yes'}
                placeholder={t('YES')}
                onSelect={(value) => handleOptionSelect('showDates', value)}
                label=""
              />
            </div>
          </div>
        </div>

        {/* ROT accommodation details — only when ROT is selected */}
        {isROTSelected && (
          <div className="pref-grid pref-rot-grid">
            {/* Accommodation type */}
            <div className="pref-card">
              <div className="pref-card-head">
                <span className="pref-card-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 11l9-7 9 7M5 10v10h14V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <div className="pref-card-titles">
                  <span className="pref-card-label">{t('ACCOMMODATION_TYPE')}</span>
                </div>
              </div>
              <div className="pref-card-control">
                <CustomDropdown
                  options={accommodationTypeOptions}
                  value={currentAccommodationType}
                  placeholder={t('APARTMENT')}
                  onSelect={(value) => handleOptionSelect('accommodationType', value)}
                  label=""
                  disabled={isEditModeInvoice}
                />
              </div>
            </div>

            {/* Apartment-specific fields */}
            {isApartmentSelected && (
              <>
                <div className="pref-card">
                  <div className="pref-card-head">
                    <span className="pref-card-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="1.5" stroke="currentColor" strokeWidth="1.8"/><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    </span>
                    <div className="pref-card-titles">
                      <span className="pref-card-label">{t('BRF_ORG_NUMBER')}
                        <Tooltip content={t('BRF_ORG_NUMBER') || 'BRF Organization Number'} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                      </span>
                    </div>
                  </div>
                  <div className="pref-card-control">
                    <Form.Control
                      type="text"
                      placeholder="200"
                      value={preferences.brfOrgNumber || ''}
                      onChange={(e) => handleOptionSelect('brfOrgNumber', e.target.value)}
                      className="input-value"
                      style={{
                        borderColor: isROTSelected && (!preferences.brfOrgNumber || !preferences.brfOrgNumber.trim()) ? '#dc3545' : undefined,
                        borderWidth: isROTSelected && (!preferences.brfOrgNumber || !preferences.brfOrgNumber.trim()) ? '2px' : undefined
                      }}
                      disabled={isEditModeInvoice}
                    />
                  </div>
                </div>
                <div className="pref-card">
                  <div className="pref-card-head">
                    <span className="pref-card-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="4" y="9" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.8"/><path d="M9 21v-4h6v4M4 9l8-5 8 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <div className="pref-card-titles">
                      <span className="pref-card-label">{t('APARTMENT_HOUSE_NO')}
                        <Tooltip content={t('APARTMENT_HOUSE_NO') || 'Apartment/House Number'} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                      </span>
                    </div>
                  </div>
                  <div className="pref-card-control">
                    <Form.Control
                      type="text"
                      placeholder="14"
                      value={preferences.apartmentHouseNo || ''}
                      onChange={(e) => handleOptionSelect('apartmentHouseNo', e.target.value)}
                      className="input-value"
                      style={{
                        borderColor: isROTSelected && (!preferences.apartmentHouseNo || !preferences.apartmentHouseNo.trim()) ? '#dc3545' : undefined,
                        borderWidth: isROTSelected && (!preferences.apartmentHouseNo || !preferences.apartmentHouseNo.trim()) ? '2px' : undefined
                      }}
                      disabled={isEditModeInvoice}
                    />
                  </div>
                </div>
              </>
            )}

            {/* House-specific field */}
            {isHouseSelected && (
              <div className="pref-card">
                <div className="pref-card-head">
                  <span className="pref-card-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 11l9-7 9 7M5 10v10h14V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  <div className="pref-card-titles">
                    <span className="pref-card-label">{t('PROPERTY')}
                      <Tooltip content={t('PROPERTY') || 'Property Details'} position="top"><span className="info-icon">ⓘ</span></Tooltip>
                    </span>
                  </div>
                </div>
                <div className="pref-card-control">
                  <Form.Control
                    type="text"
                    placeholder={t('ENTER_PROPERTY_DETAILS')}
                    value={preferences.property || ''}
                    onChange={(e) => handleOptionSelect('property', e.target.value)}
                    className="input-value"
                    style={{
                      borderColor: isROTSelected && (!preferences.property || !preferences.property.trim()) ? '#dc3545' : undefined,
                      borderWidth: isROTSelected && (!preferences.property || !preferences.property.trim()) ? '2px' : undefined
                    }}
                    disabled={isEditModeInvoice}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
    </>
  );
};

export default InvoicePreferences;